import FingerprintJS, { UnknownComponents, Confidence } from "@fingerprintjs/fingerprintjs";
import { CustomSignals } from "./structs";
import { GetAllowedCodecs } from "./codecs";
import { GetWebRTCComponent } from "./webrtc";
import { GetSensorReadings, SensorFlags } from "./sensors";

// Fingerprint agent should be loaded as soon as possible
// Disable monitoring via options
const fpLoadPromise = FingerprintJS.load({monitoring: false});
const COMPONENT_PREFIX = "C_";

const COMPONENT_KEY_SENSORS = COMPONENT_PREFIX + "sensors";

// The fingerprint result
// It has a main hash and a secondary hash based on hardware signals (more stable)
// Component will have all the built-in signals plus ones we develop ourselves
export interface FingerprintResult {
    hashMain: string
    hashesOther: {
        hashHardware: string
    }
    components: UnknownComponents
    confidence: Confidence
}

// Main function for getting fingerptinging
// Will block until agent is available and return GetResult from FingerprintJS
export async function GetFingerprint(): Promise<FingerprintResult> {
    // Load agent and get result
    const fpAgent = await fpLoadPromise.catch((e) => {
        throw e;
    });

    const result = await fpAgent.get().catch((e) => {
        throw e;
    });

    // Custom signals to be added
    const customSignals: CustomSignals = {
        webRTC: GetWebRTCComponent,
        codecs: GetAllowedCodecs,
        sensors: GetSensorReadings
    };

    // Built-in components + custom ones
    const components: UnknownComponents = {
        ...result.components
    };

    // For each available custom signal, calculate and add it
    // In case of error, return component with undefined value
    const customSignalPromises = [];
    for (const signalId in customSignals) {
        const wrapped = async () => {
                components[COMPONENT_PREFIX + signalId] = await customSignals[signalId]().catch((e) => {
                return {
                    duration: 0,
                    error: e as Error,
                };
            });
        };
        customSignalPromises.push(wrapped());
    }

    await Promise.all(customSignalPromises);
    
    // For hardware hash, do not use orientation change data from sensors, only API flag
    let sensorData = components[COMPONENT_KEY_SENSORS];

    if (sensorData.value) {
        const flags = sensorData.value as SensorFlags;
        // Create a new object to not affect the component data used for the main hash
        sensorData = {
            value: {
                hasOrientationApi: flags.hasOrientationApi,
                hasAccelerometerApi: flags.hasAccelerometerApi
            },
            duration: sensorData.duration,
        };
    }

    const hardwareComponents: UnknownComponents = {
        audio: components["audio"],
        osCpu: components["osCpu"],
        deviceMemory: components["deviceMemory"],
        hardwareConcurrency: components["hardwareConcurrency"],
        cpuClass: components["cpuClass"],
        platform: components["platform"],
        touchSupport: components["touchSupport"],
        videoCard: components["videoCard"],
        architecture: components["architecture"],
        [COMPONENT_KEY_SENSORS]: sensorData,
    };

    return {
        hashMain: FingerprintJS.hashComponents(components),
        hashesOther: {
            hashHardware: FingerprintJS.hashComponents(hardwareComponents)
        },
        components: components,
        confidence: result.confidence
    };
} 
